import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Phone, Clock } from "lucide-react";
import { SITE_PHONE_DISPLAY, SITE_PHONE_TEL } from "../config/site";

const SEEN_KEY = "oaa_exit_intent_seen";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;

    const timeout = setTimeout(() => {
      // Mobile: trigger after scroll-up after 30s OR after 60s on page
      // Desktop: trigger when mouse leaves top of viewport
      const triggerOnce = () => {
        if (sessionStorage.getItem(SEEN_KEY)) return;
        sessionStorage.setItem(SEEN_KEY, "1");
        setOpen(true);
      };

      const onMouseOut = (e) => {
        if (e.clientY <= 0 && !e.relatedTarget) triggerOnce();
      };
      const fallbackTimer = setTimeout(triggerOnce, 60000);

      document.addEventListener("mouseout", onMouseOut);
      return () => {
        document.removeEventListener("mouseout", onMouseOut);
        clearTimeout(fallbackTimer);
      };
    }, 8000);

    return () => clearTimeout(timeout);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={() => setOpen(false)}
      data-testid="exit-intent-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-bone w-full max-w-md rounded-2xl shadow-2xl border border-black/5 overflow-hidden relative animate-fade-up"
        data-testid="exit-intent-modal"
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 grid place-items-center transition z-10"
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4 text-charcoal" />
        </button>
        <div className="bg-forest text-bone px-7 pt-8 pb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-2">
            Wait — before you go
          </div>
          <h3 className="text-2xl md:text-3xl font-display font-bold leading-tight tracking-tight">
            Free 60-second case review.
            <br />Could be worth thousands.
          </h3>
        </div>
        <div className="p-7">
          <p className="text-charcoal-soft leading-relaxed mb-5">
            Most Indiana accident victims who get an attorney recover 2–3× more
            than those who don't. Don't leave money on the table.
          </p>
          <div className="flex items-center gap-2 text-xs text-charcoal-soft mb-5">
            <Clock className="w-3.5 h-3.5 text-terracotta" />
            <span>Available 24/7 · 100% free · No obligation</span>
          </div>
          <a
            href={SITE_PHONE_TEL}
            className="btn-primary w-full text-base py-3.5 mb-2"
            onClick={() => setOpen(false)}
            data-testid="exit-intent-call"
          >
            <Phone className="w-4 h-4" />
            Call {SITE_PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            className="btn-ghost w-full"
            onClick={() => setOpen(false)}
            data-testid="exit-intent-form"
          >
            Free case review
          </Link>
        </div>
      </div>
    </div>
  );
}
